import { isEqualWith, isNull } from "lodash";
import { TFunction } from "i18next";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faCcMastercard, faCcVisa, faCcDiscover, faCcDinersClub, faCcAmex } from "@fortawesome/free-brands-svg-icons";
import { faCreditCard } from "@fortawesome/pro-regular-svg-icons";
import { TProductEdit } from "pages/secure/facility/product/edit/useEditProduct";
import { IChit } from "hooks/usePOSPrinter/usePOSPrinter";
import { IAPIResponse } from "api/protocols";
import { ICourse } from "redux/reducers/models/facility";

export const displayCurrency = (currency: string, amount: number): string | null => {
  const format = new Intl.NumberFormat(new Intl.NumberFormat().resolvedOptions().locale ?? "en-CA", {
    style: "currency",
    currency: currency?.toUpperCase() ?? "CAD",
    useGrouping: false,
  }).format(Number(amount));
  return format.replace(/[A-Za-z ]/g, "");
};

export const roundNumber = (amount: any, digits: number) => {
  const convertedAmount = Number(amount);
  return convertedAmount.toFixed(digits);
};

/**
 * Provide accurate Decimal Rounding || Flooring || Ceiling to a specific number.
 * - Treats floats like decimals, fixing any binary rounding issues.
 * - decimalAdjust("round", 39.425, 1) === 39.4
 * - decimalAdjust("round", 39.425, 2) === 39.43
 * - decimalAdjust("round", 1262.48, -2) === 1300
 * @param {string} type - Math function to be applied to the given number.
 * @param {number} value - The number being rounded to a specific number of fractional digits.
 * @param {number} [precision=2] - The fractional digit precision being rounded too.
 * Passing a negative number will round places to the left of the decimal.
 */
// https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary
export const decimalAdjust = (type: "round" | "ceil" | "floor" | "trunc", value: number, precision = 2): number => {
  // Handle negative values when rounding
  if (type === "round" && value < 0) {
    return -decimalAdjust(type, -value, precision);
  }

  const shift = (num: number, exponent: number) => {
    const val = (num.toString() + "e").split("e");
    return +(val[0] + "e" + (val[1] + (exponent || 0).toString()));
  };

  const adjusted = shift(value, +precision);
  return shift(Math[type](adjusted), -precision);
};

export const roundToNearestNickel = (amount: number): number =>
  decimalAdjust("round", amount / 0.05, 0) * 0.05;

export const getDisplayCashChangeDue = (exactChangeDue: number, roundToNickel: boolean): number =>
  roundToNickel ? roundToNearestNickel(exactChangeDue) : exactChangeDue;

export const displayPercent = (amount: number | string, precision = 2) => {
  amount = typeof amount === "string" ? Number(amount) * 100 : amount * 100;
  return String(decimalAdjust("round", amount, precision)) + "%";
  // return String(amount) + "%";
};

export const formatDate = (date: Date) => {
  //1969-12-31
  const d = new Date(date);
  let month = "" + String(d.getMonth() + 1);
  let day = "" + String(d.getDate());
  const year = d.getFullYear();

  if (month.length < 2) {
    month = "0" + month;
  }
  if (day.length < 2) {
    day = "0" + day;
  }

  return [year, month, day].join("-");
};

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC off-by-one in date pickers). */
export function parseLocalDateString(date?: string | null): Date | null {
  if (!date) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date));
  if (!match) {
    return null;
  }

  return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
};

export const convertTime = (startTime: any, omitPeriod?: boolean, includeLeadingZero?: boolean) => {
  if (!startTime) {
    return "";
  }
  const tempHours = startTime?.split(":");
  let hours = tempHours[0];
  const minutes = tempHours[1];
  let startTimeString = "";

  if (hours < 12) {
    hours = Number(hours);
    startTimeString =
      (includeLeadingZero && hours < 10 ? "0" : "") + String(hours) + ":" + String(minutes) + (omitPeriod ? "" : " AM");
    //startTimeString = startTime + " AM";
  } else {
    hours = hours - 12;

    if (hours === 0) {
      hours = 12;
    }

    startTimeString =
      (includeLeadingZero && hours < 10 ? "0" : "") + String(hours) + ":" + String(minutes) + (omitPeriod ? "" : " PM");
  }

  return startTimeString;
};

export const timeToNumber = () => {
  const curr = new Date();
  let hour = `${curr.getHours()}`;
  let min = `${curr.getMinutes()}`;

  if (hour.length < 2) {
    hour = "0" + hour;
  }

  if (min.length < 2) {
    min = "0" + min;
  }

  const parsedTime = parseInt(`${hour}${min}`);

  return parsedTime;
};

export const capitalize = (str: string) => {
  if (typeof str === "string") {
    return str.replace("_", "").charAt(0).toUpperCase() + str.slice(1);
  } else {
    return "";
  }
};

export function capitalizeWords(string: string) {
  const stringArray = string.toLowerCase().split(/[ -]/);
  stringArray.forEach((word, index) => (stringArray[index] = word?.charAt(0).toUpperCase() + word.substring(1)));
  return stringArray.join(" ");
}

export const uppercase = (str: string) => {
  if (typeof str === "string") {
    return str.toUpperCase();
  } else {
    return "";
  }
};

export const valueToString = (str: string, style?: string) => {
  if (str === undefined || str === null) {
    return;
  }

  const result = str.replace(/_/g, " ");
  if (style === "uppercase") {
    return result.toUpperCase();
  } else {
    return result.replace(/(^\w|\s\w)/g, (c: string) => c.toUpperCase());
  }
};

export const delay = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export function isNumeric(str: string) {
  return !Number.isNaN(Number.parseFloat(str)) && Number.isFinite(Number.parseFloat(str));
}

export function isPositiveInteger(str: string) {
  const positiveIntegerRegex = /^[1-9][0-9]*$/;
  return positiveIntegerRegex.test(str);
}

export function isWholeNumber(str: string) {
  const wholeNumberRegex = /^0$|^[1-9][0-9]*$/;
  return wholeNumberRegex.test(str);
}


// Abbreviated label shown on floor-plan / table-service tables.
export function deriveTableShortTitle(title: string | null | undefined): string {
  if (!title || String(title).startsWith(" ")) {
    return "";
  }

  // Keep purely numeric titles intact so zeros are never dropped (e.g. "101", "20").
  if (/^\d+$/.test(title)) {
    return title;
  }

  const firstCharacter = title[0];
  const digitsAfterFirst = /([0-9]+)/.exec(title.slice(1))?.[0] ?? "";
  return firstCharacter + digitsAfterFirst;
}

export const isEmpty = (x: any) => x === undefined || x === null || x === "";

/**
 * @param {string} date Formatted string: YYYY-MM-DD
 * @returns {string} Formatted string
 */
export function formatDateFromString(date: string) {
  const tempDateArray = date.split("-");

  const tempYear: number = parseInt(tempDateArray[0]);
  const tempMonth: number = parseInt(tempDateArray[1]) - 1;
  const tempDay: number = parseInt(tempDateArray[2]);

  //build the full date
  const d = new Date(tempYear, tempMonth, tempDay);

  const weekday = d.toLocaleString("en-us", { weekday: "long" });
  const month = d.toLocaleString("en-us", { month: "long" });
  const day = d.toLocaleString("en-us", { day: "numeric" });
  const year = d.toLocaleString("en-us", { year: "numeric" });

  const dateString = weekday + " " + month + " " + day + ", " + year;
  return dateString;
}

export const monthNames = (t: TFunction<"translation", undefined>) => [
  `${t("helpers.helpers.005")}`, //January
  `${t("helpers.helpers.006")}`, //February
  `${t("helpers.helpers.007")}`, //March
  `${t("helpers.helpers.008")}`, //April
  `${t("helpers.helpers.009")}`, //May
  `${t("helpers.helpers.010")}`, //June
  `${t("helpers.helpers.011")}`, //July
  `${t("helpers.helpers.012")}`, //August
  `${t("helpers.helpers.013")}`, //September
  `${t("helpers.helpers.014")}`, //October
  `${t("helpers.helpers.015")}`, //November
  `${t("helpers.helpers.016")}`, //December
];

export const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Returns a new sorted obj.
 *   - Type passed in is the type returned as an array.
 */
export function sortObj<T>(obj: T[], key: keyof T, direction: "asc" | "desc" = "asc"): T[] {
  return [...obj].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    const aEmpty = aVal === null || aVal === undefined || aVal === "";
    const bEmpty = bVal === null || bVal === undefined || bVal === "";

    if (aEmpty && bEmpty) {
      return 0;
    }
    // Keep empty values at the end regardless of sort direction
    if (aEmpty) {
      return 1;
    }
    if (bEmpty) {
      return -1;
    }

    if (aVal > bVal) {
      return direction === "asc" ? 1 : -1;
    }
    if (aVal < bVal) {
      return direction === "asc" ? -1 : 1;
    }
    return 0;
  });
}

/**
 * Given obj keys, determine if the value at the specific key is empty.
 *  - Used across app to validate keys required before calling POST.
 *  - Uses isEmpty helper function.
 *  - Treats -1 values as empty.  Generally -1 = 'empty' inputs
 */
export function validateRequired(
  obj: TProductEdit,
  requiredKeys: Array<keyof TProductEdit | "cost_with_track_inventory">,
): boolean {
  const emptyRequiredKeys = requiredKeys.filter(
    key =>
      isEmpty(obj[key as keyof TProductEdit]) ||
      (typeof obj[key as keyof TProductEdit] == "number" && obj[key as keyof TProductEdit] == -1) ||
      (typeof obj[key as keyof TProductEdit] === "number" && !numberIsValid(obj[key as keyof TProductEdit] as string)),
  );

  if (emptyRequiredKeys.length === 0) {
    return true;
  }
  // if track_inventory true, only allow save IF product cost is present.
  if (emptyRequiredKeys.includes("cost_with_track_inventory") && !obj.track_inventory) {
    return true;
  } else {
    if (!isEmpty(obj.cost)) {
      return true;
    }
  }

  return false;
}

/**
 * @param requirementObj Base object representing required inputs, and if being required is active.
 * @param splitBy string value removed from the requirementObj keys.  Backend returns the keys structured like "${splitBy}required_key"
 * @returns
 *    - generateRequiredKeys<TPostProduct>({ product_title: false, product_cost: true, product_subtitle: true }, "product_");
 *    - returns [ "cost", "subtitle" ]
 */
export function generateRequiredKeys<T = Record<string, boolean>>(
  requirementObj: Record<string, boolean>,
  splitBy: string,
): Array<keyof T | "cost_with_track_inventory"> {
  if (!requirementObj) {
    return [];
  }

  const activeRequirements = Object.keys(requirementObj)
    .map(key => {
      if (requirementObj[key]) {
        return key;
      }
      if (key.includes("cost_with_track_inventory")) {
        return key;
      }
    })
    .filter(val => val !== undefined);

  return activeRequirements.map(key => key.split(splitBy).pop() as keyof T | "cost_with_track_inventory");
}

export function customerErrorMessage(t: TFunction<"translation", undefined>, message: string) {
  switch (message) {
    case "Facility does not exist":
      return `${t("helpers.helpers.001")}`;
    case "Client does not exist":
      return `${t("helpers.helpers.002")}`;
    case "Customer already exists":
      return `${t("helpers.helpers.003")}`;
    case "A phone number or email is required":
      return `${t("helpers.helpers.004")}`;
    default:
      return message;
  }
}

export function unsavedChangesExist<T>(
  wanted: T,
  current: T,
  setCurrent?: React.Dispatch<React.SetStateAction<T>>,
  loaded = true,
) {
  if (isEmpty(current)) {
    if (loaded && setCurrent) {
      setCurrent(current);
    }
    return false;
  }

  return !isEqualWith(current, wanted, (originalValue, newValue) => {
    if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
      return true;
    }
  });
}

/**
 * A function to compare if two arrays have the same elements regardless of their order
 *   - ie (TRUE)
 *   - a = [ 1, 2, 3 ]
 *   - b = [ 2, 1, 3 ]
 * */
export function ignoreOrderCompare<T>(a: T[], b: T[]) {
  if (isEmpty(a) || isEmpty(b) || a?.length !== b?.length) {
    return false;
  }

  const elements = new Set([...a, ...b]);
  for (const x of elements) {
    const count1 = a.filter(e => e === x).length;
    const count2 = b.filter(e => e === x).length;
    if (count1 !== count2) {
      return false;
    }
  }

  return true;
}

export function getLanguage() {
  const selectedLanguage = localStorage.getItem("i18nextLng");
  switch (selectedLanguage) {
    case "fr":
      return "French";
    case "frCA":
      return "French";
    case "en":
      return "English";
    default:
      return "English";
  }
}

export function getOpenWeatherLang(i18nLanguage?: string): string {
  const lang = i18nLanguage ?? localStorage.getItem("i18nextLng") ?? "en-CA";
  if (lang.startsWith("fr")) {
    return "fr";
  }
  return "en";
}

export function getCreditCardIcon(brand: string) {
  let iconName: IconDefinition = null;
  switch (brand) {
    case "visa":
      iconName = faCcVisa;
      break;

    case "amex":
      iconName = faCcAmex;
      break;

    case "mastercard":
      iconName = faCcMastercard;
      break;

    case "discover":
      iconName = faCcDiscover;
      break;

    case "diners":
      iconName = faCcDinersClub;
      break;

    default:
      iconName = faCreditCard;
      break;
  }
  return iconName;
}

/**
 * Update the state passed given an input onChange event.
 * ID is required on the input.
 *
 * @param event  onChange event from the Input or TextField element.
 * @param state  state object being affected by the input.
 * @param setState state setter dispatch.
 *
 * Note: type of the value depends how to handle the updated value.
 * - string = event.target.value
 * - number = parseInt(event.target.value)
 * - boolean = event.target.checked
 */
export function handleChangeEventInput<T extends Record<string, any>>(
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  state: T,
  setState: React.Dispatch<React.SetStateAction<T>>,
) {
  const { id, value } = event.target;
  const valueType = typeof state[id as keyof T];

  if (!valueType) {
    console.warn(`Check ID prop on Input:  \n${id} was not found.`, state);
    return;
  }

  setState(prev => ({
    ...prev,
    [id]:
      valueType === "number"
        ? parseInt(value)
        : valueType === "boolean"
        ? (event.target as HTMLInputElement).checked
        : value,
  }));
}

/**
 * Helper to check value is a number and greater than 0 if it is required.
 * @param value string or number value to check validity
 * @param optional null | undefined | value.length == 0 also returns true.
 * */
export function numberIsValid(value: string | number, optional?: boolean) {
  const price = Number(value);

  // Optional value
  if (optional) {
    if (value === null || value === undefined || String(value).length === 0) {
      return true;
    } else {
      return !isNaN(price) && price >= 0;
    }
  }

  // Required value
  return value !== null && value !== undefined && value !== "" && !isNaN(price) && price >= 0;
}

/** Given a valid date, return the time of that date as a string. */
export function generateTime(date: Date): string {
  if (date) {
    const dateTimeArray = new Date(date).toTimeString().split(" ");
    const timeArray = dateTimeArray[0].split(":");

    return timeArray[0] + ":" + timeArray[1];
  }

  return null;
}

/**
 * Generate nested array to allow simple grid display.
 * @param {T} array Value being mapped into a grid.
 * @param {number} newRowOn Number of columns wanted for the grid.
 * @example
 *   {arrayAsGrid(myColorArray, 2).map((row, rowIndex) => (
 *      <div 
            key={rowIndex} 
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}
        >
            {row.map((color: string) => (
                <p>{color}</p>
            ))}
 *      </div>
 *   ))}
*/
export const arrayAsGrid = <T>(array: T[], newRowOn: number) => {
  const arr = array.slice();
  const chunks = [];
  while (arr.length) {
    chunks.push(arr.splice(0, newRowOn));
  }
  return chunks;
};

/**
 * Returns a string of `1fr` dependant on length of columnCount param
 * @example
 *   arrayAsColumnTemplate(3) = '1fr 1fr 1fr'
 */
export const arrayAsColumnTemplate = (columnCount: number) => {
  const temp: string[] = new Array(columnCount).fill("1fr");
  const frameString = temp.toString().replace(/,/g, " ");

  return frameString;
};

/**
 *
 * @param hexColor
 * @returns Brightness with 0 being completely dark, 50 in the middle, and 100 being completely bright.
 * Returns null if the hex color param is invalid. It ignores the alpha value.
 */
export function calculateHexColorBrightness(hexColor: string): number {
  // Linearizes a given RGB component value for luminance calculation
  function linearizeColorComponent(colorComponent: number) {
    const normalizedColorComponent = colorComponent / 255;

    if (normalizedColorComponent <= 0.04045) {
      return normalizedColorComponent / 12.92;
    } else {
      return Math.pow((normalizedColorComponent + 0.055) / 1.055, 2.4);
    }
  }

  // Calculates the L* value from the CIE XYZ luminance
  function calculateLStar(luminance: number) {
    if (luminance <= 216 / 24389) {
      return luminance * (24389 / 27);
    } else {
      return Math.pow(luminance, 1 / 3) * 116 - 16;
    }
  }

  // Ignores alpha value
  let hexColorToTransform = hexColor?.substring(0, 7);

  // Expands shorthand hex color if necessary
  if (hexColorToTransform?.length === 4) {
    hexColorToTransform =
      hexColor[0] + hexColor[1] + hexColor[1] + hexColor[2] + hexColor[2] + hexColor[3] + hexColor[3];
  }

  const hexToRgbRegex = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/;
  const hexToRgbMatch = hexToRgbRegex.exec(hexColorToTransform);

  if (hexToRgbMatch) {
    const [, redHex, greenHex, blueHex] = hexToRgbMatch;
    const linearRed = linearizeColorComponent(parseInt(redHex, 16));
    const linearGreen = linearizeColorComponent(parseInt(greenHex, 16));
    const linearBlue = linearizeColorComponent(parseInt(blueHex, 16));

    const luminance = linearRed * 0.2126 + linearGreen * 0.7152 + linearBlue * 0.0722;

    const lStar = calculateLStar(luminance);

    return lStar;
  } else {
    return null;
  }
}

/**
 * Prints a receipt through the desktop printer application.
 * If communication fails, it will fall back to the browser.
 *
 * @param receipt
 */
export function printDesktopReceipt(facilityId: number, receipt: string) {
  let printer: any;

  try {
    printer = JSON.parse(localStorage.getItem(`printer_${facilityId}`));

    if (!printer) {
      throw new Error("No printer found");
    }
  } catch {
    window.open().document.write(receipt);
    return;
  }

  const socket = new WebSocket("ws://localhost:80");

  socket.onerror = () => {
    window.open().document.write(receipt);
  };

  socket.addEventListener("open", () => {
    socket.send(
      JSON.stringify({
        printer_id: printer?.id,
        printer_name: printer?.device_name,
        html_receipt: receipt,
      }),
    );
  });
}

/**
 * Prints a kitchen chit through the desktop printer application.
 * If communication fails, or if no printer is attached to the chit,
 * it will reject the promise.
 *
 * @param chit IChit object
 */
export function printDesktopKitchenChit(chit: IChit, t: TFunction) {
  return new Promise<void>((resolve: () => void, reject: (reason?: any) => void) => {
    const printer = chit.printer;

    // If the chit has already been sent to the printer as a job,
    // don't send it again.
    if (chit.enqueued_printer_job) {
      return;
    }

    if (!printer) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(t("helpers.helpers.017"));
      return;
    }

    const socket = new WebSocket("ws://localhost:80");

    socket.onerror = () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(t("helpers.helpers.018"));
    };

    socket.addEventListener("open", () => {
      socket.send(
        JSON.stringify({
          kitchen_chit: chit,
        }),
      );

      resolve();
    });
  });
}

export function errorMessage(response: IAPIResponse, defaultMessage: string): string {
  return typeof response?.data === "string"
    ? response?.data
    : (response?.data?.message as string)
    ? (response?.data?.message as string)
    : defaultMessage;
}

export function openPrintWindow(printContent: string) {
  window.open().document.write(printContent);
}

export function downloadCsv(content: string, filename: string): void {
  const link = document.createElement("a");
  link.href = "data:text/csv;charset=utf-8," + encodeURI(content);
  link.target = "_blank";
  link.download = filename;
  link.click();
}

export function convertHoles(holes: number, course: ICourse) {
  if (course?.holes === 12) {
    return holes === 9 ? 6 : 12;
  }

  if (course?.holes === 24) {
    return holes === 9 ? 12 : 24;
  }

  return holes;
}

export function AppIsDevelopment() {
  return process.env.BASE_URL === "http://localhost:7086/" || process.env.BASE_URL === "https://admin-dev.teeon.com/";
}

export function productTypeGroups(t: TFunction) {
  return [
    {id: "none", label: t("None")},
    {id: "alcohol", label: t("Alcohol")},
    {id: "food", label: t("Food")},
    {id: "beverages", label: t("Beverages")},
  ];
}
